<template>
  <form
    class="form-advance-withdrawal"
    v-on:submit.prevent="onSubmit"
  >
    <the-select
      v-if="isShowCashboxList"
      option-title-key="name"
      option-value-key="id"
      placeholder="Выберите кассу"
      v-bind:optional="false"
      v-bind:options="cashboxList"
      v-model="selectedCashboxId"
    />
    <template v-if="selectedCashbox">
      <label class="form-advance-withdrawal__initial-balance">
        Текущий остаток:
        <input
          disabled="disabled"
          type="text"
          v-bind:value="initialBalanceTitle" />
      </label>
      <input-number
        min="0"
        title="Введите сумму списания, руб"
        v-bind:max="initialBalance"
        v-model="amountRub"
        without-native-controls />
      <output class="form-advance-withdrawal__balance-after">
        Остаток после списания:
        <the-rub
          currency="руб"
          show-kop="auto"
          v-bind:kop="balanceKopAfter" />
      </output>
      <input-radio-with-bullets
        identifier="withdrawal-mode"
        v-bind:items="$options.withdrawalModes"
        v-model="selectedWithdrawalMode"
      />
    </template>
    <button
      class="form-advance-withdrawal__submit"
      type="submit"
      v-bind:disabled="isSubmitButtonDisabled"
    >Списать</button>
  </form>
</template>
<script>
import { AdvanceWithdrawal } from '@/mixins/advanceWithdrawMixin';
import InputNumber from '@/components/InputNumber.vue';
import TheRub from '@/components/TheRub.vue';
import TheSelect from '@/components/TheSelect.vue';
import InputRadioWithBullets from '@/components/InputRadioWithBullets.vue';

/**
 * @description Способы списания.
 * @type {any[]}
 */
const WITHDRAWAL_MODES = [
  { title: 'Наличными', value: 'cash' },
  { title: 'На карту', value: 'cashless' },
];

/**
 * @description Форма для списания аванса пациента.
 * @type {Vue}
 */
export default {
  components: {
    InputNumber,
    TheRub,
    TheSelect,
    InputRadioWithBullets,
  },
  computed: {
    selectedCashbox() {
      if (this.selectedCashboxId) {
        return this.cashboxList.find(({ id }) => id === Number(this.selectedCashboxId));
      }
      return null;
    },
    initialBalanceTitle() {
      if (this.selectedCashbox) {
        return this.selectedCashbox.balance_rubles_title;
      }
      return '';
    },
    initialBalance() {
      if (this.selectedCashbox) {
        return this.selectedCashbox.balance_rubles;
      }
      return '';
    },
    /**
     * @description Остаток за вычетом списания в копейках.
     */
    balanceKopAfter() {
      if (!this.selectedCashbox) {
        return 0;
      }
      const initialBalance = this.selectedCashbox.balance_rubles;
      if (this.amountRub < 0) {
        return Number(initialBalance);
      }
      const availableAmountRub = Number(this.amountRub) < Number(initialBalance)
        ? this.amountRub
        : initialBalance;
      return 100 * (initialBalance - availableAmountRub);
    },
    /**
     * @description Показывать список касс.
     * @type {boolean}
     */
    isShowCashboxList() {
      return this.cashboxList.length > 1;
    },
    isSubmitButtonDisabled() {
      return this.isAwaitRequest || !this.selectedCashboxId || !this.selectedWithdrawalMode;
    },
  },
  data() {
    let selectedCashboxId = null;
    const defaultCashbox = this.cashboxList.find(
      (candidate) => candidate.tags.includes('default'),
    );
    if (!defaultCashbox) {
      this.cashboxList.slice(0, 1).forEach((cashbox) => {
        selectedCashboxId = cashbox.id;
      });
    } else {
      selectedCashboxId = defaultCashbox.id;
    }
    return {
      /**
       * @description Сумма списания.
       * @type {String}
       */
      amountRub: '0',
      /**
       * @description Идентификатор выбранной кассы.
       * @type {any}
       */
      selectedCashboxId,
      /**
       * @description Выбранный способ списания.
       * @type {string|null}
       */
      selectedWithdrawalMode: null,
    };
  },
  methods: {
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const withdrawal = new AdvanceWithdrawal(
        this.selectedCashboxId,
        this.amountRub,
        this.selectedWithdrawalMode,
      );
      this.$emit('submit', withdrawal);
    },
  },
  name: 'FormAdvanceWithdrawal',
  props: {
    /**
     * @description Список касс.
     */
    cashboxList: {
      required: true,
      type: Array,
    },
    /**
     * @description Запрос выполняется.
     * @type {boolean}
     */
    isAwaitRequest: { type: Boolean, default: false },
  },
  withdrawalModes: WITHDRAWAL_MODES,
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";

.form-advance-withdrawal {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  & &__balance-after {
    font-size: .825rem;
    font-style: italic;
  }
  & &__balance-after,
  & &__submit {
    margin-left: auto;
  }
  & &__submit {
    @include buttons.btn-blue(10rem, 44px, 1rem);
    &:disabled {
      background-color: var(--unfocus-color);
      border: 1px solid var(--unfocus-color);
      color: white;
    }
  }
}
</style>
